import React from 'react';
import { Server } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { OptimizedImage } from '@/components/OptimizedImage';
import { supabase } from '@/integrations/supabase/client';

interface ServerLogoWithFallbackProps {
  serverId: string;
  name: string;
  logoImageUrl?: string | null;
  gameFallbackImage?: string | null;
  gameName?: string;
  size?: number;
  eager?: boolean;
  className?: string;
  borderClassName?: string;
}

// Vérifie qu'une image se charge et n'est pas un placeholder générique (globe Google 16px)
function validateImage(url: string, minSize = 48): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image();
    const timer = setTimeout(() => resolve(false), 4000);
    img.onload = () => {
      clearTimeout(timer);
      resolve(img.naturalWidth >= minSize && img.naturalHeight >= minSize);
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(false);
    };
    img.src = url;
  });
}

// Résout un logo de repli en scrapant Discord (icône du serveur) puis le favicon du site web
async function resolveExternalLogo(serverId: string): Promise<string | null> {
  const { data } = await supabase
    .from('servers')
    .select('discord_url, website_url')
    .eq('id', serverId)
    .maybeSingle();
  if (!data) return null;

  if (data.discord_url) {
    const match = data.discord_url.match(/(?:discord\.gg|discord\.com\/invite)\/([a-zA-Z0-9-]+)/i);
    if (match) {
      try {
        const res = await fetch(`https://discord.com/api/v10/invites/${match[1]}`);
        if (res.ok) {
          const j = await res.json();
          if (j?.guild?.id && j?.guild?.icon) {
            const iconUrl = `https://cdn.discordapp.com/icons/${j.guild.id}/${j.guild.icon}.png?size=256`;
            if (await validateImage(iconUrl, 64)) return iconUrl;
          }
        }
      } catch {
        // silencieux — on tentera le favicon site web
      }
    }
  }

  if (data.website_url) {
    try {
      const url = data.website_url.startsWith('http') ? data.website_url : `https://${data.website_url}`;
      const host = new URL(url).hostname;
      if (host) {
        const faviconUrl = `https://www.google.com/s2/favicons?domain=${host}&sz=128`;
        if (await validateImage(faviconUrl, 48)) return faviconUrl;
      }
    } catch {
      // ignore
    }
  }

  return null;
}

/**
 * Multi-stage logo fallback chain: uploaded logo → Discord icon → website favicon →
 * game backdrop → generic placeholder. Used by ServerCard and the server detail page
 * so both surfaces show the exact same visual for servers without an uploaded logo.
 */
export const ServerLogoWithFallback: React.FC<ServerLogoWithFallbackProps> = ({
  serverId,
  name,
  logoImageUrl,
  gameFallbackImage,
  gameName,
  size = 110,
  eager = false,
  className = 'w-full h-full object-cover',
  borderClassName = '',
}) => {
  const { data: externalLogo } = useQuery({
    queryKey: ['external-logo', serverId],
    queryFn: () => resolveExternalLogo(serverId),
    enabled: !logoImageUrl,
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24,
    retry: false,
  });

  const genericFallback = (
    <div className="w-full h-full bg-slate-700/80 flex items-center justify-center text-slate-400">
      <Server className="w-1/3 h-1/3" aria-hidden="true" />
    </div>
  );

  const gameBasedFallback = gameFallbackImage ? (
    <OptimizedImage
      src={gameFallbackImage}
      alt={`${gameName || 'Game'} - ${name}`}
      serverName={name}
      imageType="logo"
      width={size}
      height={size}
      loading={eager ? 'eager' : 'lazy'}
      priority={eager}
      className={className}
      fallback={genericFallback}
    />
  ) : genericFallback;

  const externalLogoFallback = externalLogo ? (
    <OptimizedImage
      src={externalLogo}
      alt={`Logo ${name}`}
      serverName={name}
      imageType="logo"
      width={size}
      height={size}
      loading={eager ? 'eager' : 'lazy'}
      priority={eager}
      className={`${className} ${borderClassName}`.trim()}
      fallback={gameBasedFallback}
    />
  ) : gameBasedFallback;

  if (logoImageUrl) {
    return (
      <OptimizedImage
        src={logoImageUrl}
        alt={`Logo ${name}`}
        serverName={name}
        imageType="logo"
        width={size}
        height={size}
        loading={eager ? 'eager' : 'lazy'}
        priority={eager}
        className={`${className} ${borderClassName}`.trim()}
        fallback={externalLogoFallback}
      />
    );
  }

  return externalLogoFallback;
};
